import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { auth as authApi } from '../services/api';
import PhoneInput from 'react-phone-number-input';
import 'react-phone-number-input/style.css';
import { isValidPhoneNumber } from 'react-phone-number-input';

interface LoginModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void; // Callback for when login/register is successful
}

const LoginModal: React.FC<LoginModalProps> = ({ isOpen, onClose, onSuccess }) => {
  const { t, i18n } = useTranslation();
  const { login, loginWithToken } = useAuth();
  const [showRegister, setShowRegister] = useState(false);
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  const [loginLoading, setLoginLoading] = useState(false);
  const [registerName, setRegisterName] = useState('');
  const [registerEmail, setRegisterEmail] = useState('');
  const [registerPhone, setRegisterPhone] = useState<string | undefined>('');
  const [registerPassword, setRegisterPassword] = useState('');
  const [registerErrors, setRegisterErrors] = useState<{ [key: string]: string[] | string }>({});
  const [registerLoading, setRegisterLoading] = useState(false);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoginLoading(true);
    setLoginError('');
    try {
      await login(loginEmail, loginPassword);
      setShowRegister(false);
      onClose();
      onSuccess?.(); // Call the success callback
    } catch (err: any) {
      if (err.response && err.response.data && typeof err.response.data === 'string') {
        setLoginError(err.response.data);
      } else {
        setLoginError(t('errors.loginFailed'));
      }
    } finally {
      setLoginLoading(false);
    }
  };

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    setRegisterLoading(true);
    setRegisterErrors({});

    if (!registerPhone || !isValidPhoneNumber(registerPhone)) {
      setRegisterErrors({ PhoneNumber: ["Please enter a valid phone number."] });
      setRegisterLoading(false);
      return;
    }

    try {
      const { token } = await authApi.register({
        name: registerName,
        email: registerEmail,
        phoneNumber: registerPhone,
        password: registerPassword
      });
      loginWithToken(token);
      setShowRegister(false);
      onClose();
      onSuccess?.(); // Call the success callback
    } catch (error: any) {
      if (error.response && error.response.data && error.response.data.errors) {
        setRegisterErrors(error.response.data.errors);
      } else if (error.response && error.response.data && typeof error.response.data === 'string') {
        setRegisterErrors({ general: error.response.data });
      } else {
        setRegisterErrors({ general: t('errors.networkError') });
      }
    } finally {
      setRegisterLoading(false);
    }
  };

  const handleClose = () => {
    setShowRegister(false);
    setLoginError('');
    setRegisterErrors({});
    setLoginEmail('');
    setLoginPassword('');
    setRegisterName('');
    setRegisterEmail('');
    setRegisterPhone('');
    setRegisterPassword('');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-lg p-8 w-full max-w-sm relative" dir={i18n.language === 'he' ? 'rtl' : 'ltr'}>
        <button 
          className={`absolute top-2 text-gray-500 hover:text-gray-800 text-2xl ${i18n.language === 'he' ? 'left-2' : 'right-2'}`} 
          onClick={handleClose}
        >
          &times;
        </button>
        {!showRegister ? (
          <>
            <h2 className="text-2xl font-bold mb-4 text-center">{t('nav.login')}</h2>
            <form onSubmit={handleLogin} className="space-y-4">
              <input
                type="email"
                className="w-full px-3 py-2 border rounded-lg"
                placeholder={t('contact.email')}
                value={loginEmail}
                onChange={e => setLoginEmail(e.target.value)}
                required
              />
              <input
                type="password"
                className="w-full px-3 py-2 border rounded-lg"
                placeholder={t('nav.password')}
                value={loginPassword}
                onChange={e => setLoginPassword(e.target.value)}
                required
              />
              {loginError && <div className="text-red-600 text-center">{loginError}</div>}
              <button
                type="submit"
                className="w-full bg-blue-600 text-white font-bold py-2 px-4 rounded-lg hover:bg-blue-700 transition"
                disabled={loginLoading}
              >
                {loginLoading ? t('common.loading') : t('nav.login')}
              </button>
            </form>
            <div className="text-center mt-4">
              <p>{t('nav.noAccount')} <button className="text-blue-600 hover:underline" onClick={() => { setShowRegister(true); setRegisterErrors({}); }}>{t('nav.register')}</button></p>
            </div>
          </>
        ) : (
          <>
            <h2 className="text-2xl font-bold mb-4 text-center">{t('nav.register')}</h2>
            <form onSubmit={handleRegister} className="space-y-4">
              <div>
                <label className="block text-sm font-medium">{t('form.name')}</label>
                <input type="text" value={registerName} onChange={e => setRegisterName(e.target.value)} className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2" required />
                {registerErrors.Name && <div className="text-red-600 text-sm mt-1">{registerErrors.Name[0]}</div>}
              </div>
              <div>
                <label className="block text-sm font-medium">{t('form.email')}</label>
                <input type="email" value={registerEmail} onChange={e => setRegisterEmail(e.target.value)} className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2" required />
                {registerErrors.Email && <div className="text-red-600 text-sm mt-1">{registerErrors.Email[0]}</div>}
              </div>
              <div>
                <label className="block text-sm font-medium">{t('form.phone')}</label>
                <PhoneInput
                  international
                  defaultCountry="IL"
                  value={registerPhone}
                  onChange={setRegisterPhone}
                  className="mt-1"
                />
                {registerErrors.PhoneNumber && <div className="text-red-600 text-sm mt-1">{Array.isArray(registerErrors.PhoneNumber) ? registerErrors.PhoneNumber[0] : registerErrors.PhoneNumber}</div>}
              </div>
              <div>
                <label className="block text-sm font-medium">{t('form.password')}</label>
                <input type="password" value={registerPassword} onChange={e => setRegisterPassword(e.target.value)} className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2" required />
                {registerErrors.Password && <div className="text-red-600 text-sm mt-1">{registerErrors.Password[0]}</div>}
              </div>
              {registerErrors.general && <div className="text-red-600 text-center">{registerErrors.general}</div>}
              <button type="submit" disabled={registerLoading} className="w-full bg-blue-600 text-white p-2 rounded-md hover:bg-blue-700 disabled:bg-gray-400">
                {registerLoading ? t('common.loading') : t('nav.register')}
              </button>
            </form>
            <div className="text-center mt-4">
              <button className="text-blue-600 hover:underline" onClick={() => { setShowRegister(false); setRegisterErrors({}); }}>
                {t('nav.haveAccount')}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default LoginModal; 